import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

/**
 * Factory that generates a paired `useDetail` / `useDetailSuspense` hook
 * from a single query-key + fetch-fn definition.
 *
 * - `useDetail(id?)` — conditional fetch via `useQuery` + `enabled`.
 *   Returns `data: T | undefined`. Use in parents that may not have an ID yet.
 *
 * - `useDetailSuspense(id)` — guaranteed fetch via `useSuspenseQuery`.
 *   Returns `data: T`. Must be rendered inside a `<Suspense>` boundary.
 *
 * Both hooks share the same query key, so they read from the same cache entry.
 *
 * @example
 * ```ts
 * export const [useGoal, useGoalSuspense] = createDetailHooks(
 *   goalKeys.detail,
 *   goalService.getById,
 * );
 * ```
 */
export function createDetailHooks<T>(
  keyFn: (id: string) => readonly unknown[],
  fetchFn: (id: string) => Promise<T>,
) {
  function useDetail(id: string | undefined) {
    return useQuery({
      queryKey: keyFn(id!),
      queryFn: () => fetchFn(id!),
      enabled: !!id,
    });
  }

  function useDetailSuspense(id: string) {
    return useSuspenseQuery({
      queryKey: keyFn(id),
      queryFn: () => fetchFn(id),
    });
  }

  return [useDetail, useDetailSuspense] as const;
}
